import type { ToggleMetadata } from '@restart/ui/Dropdown';
import ReactBootstrapDropdown from 'react-bootstrap/Dropdown';

import React from 'react';
import classNames from 'classnames';
import SelectableContext from '@restart/ui/SelectableContext';

type Booleanish = boolean | 'true' | 'false';

import styles from 'css/src/components/common/ui/Dropdown.module.scss';

import type { DebouncedFunc } from 'lodash';
import debounce from 'lodash/debounce';

import Button, { SPINNER_ICON } from 'js/src/components/common/ui/Button';
import Spinner from 'js/src/components/common/ui/Spinner';
import type { Placement } from 'js/src/components/common/ui/Tooltip';
import Tooltip from 'js/src/components/common/ui/Tooltip';
import type { DROPDOWN_ITEM_TEXT_STYLES } from 'js/src/libs/consts';
import BlueprintsIcon from 'js/src/libs/custom_icons/BlueprintsIcon';
import SmartEditIcon from 'js/src/libs/custom_icons/SmartEditIcon';
import SmartMediaIcon from 'js/src/libs/custom_icons/SmartMediaIcon';
import SmartSearchIcon from 'js/src/libs/custom_icons/SmartSearchIcon';
import uuid from 'js/src/libs/uuid';

const CUSTOM_ICONS_MAP: {
    [key: string]: React.ReactNode;
} = {
    blueprints: <BlueprintsIcon />,
    smart_search: <SmartSearchIcon fillColor="currentColor" />,
    smart_edit: <SmartEditIcon />,
    smart_media: <SmartMediaIcon />,
};

// delay time for sub menus to open/close, allows for accidental hovers
// https://lumen5.slack.com/archives/C0730L096JK/p1717783045215269
const MOUSEENTER_DELAY_MS = 250;
type SetMenuPathCallback = (path: string) => void;
/**
 * To allow for accidental hovers to be ignored, we need to implement a debounce
 * on the opening/closing of submenus. This class maintains the callbacks
 * so that they are cancellable depending on situation.
 */
class DebouncedSetMenuPathManager {
    debounceTime: number;
    callbacks: Map<string, DebouncedFunc<SetMenuPathCallback>>;

    constructor(debounceTime: number) {
        this.debounceTime = debounceTime;
        this.callbacks = new Map();
    }

    _createKey(path: string) {
        return `${JSON.stringify(path).replace(/"/g, '')}*${uuid()}`;
    }

    _getPath(key: string) {
        return key.split('*')[0];
    }

    addCallback(callback: SetMenuPathCallback, debounceTime: number): SetMenuPathCallback {
        const debouncedCallback = debounce(callback, debounceTime);

        return (path) => {
            const argsKey = this._createKey(path);

            // rootClose called, prioritize this over other queued callbacks
            if (this._getPath(argsKey) === '') {
                this.cancelQueuedCallbacks();
                callback(path);
                return;
            }

            // Set the path as the key to the debounced callback in the map
            this.callbacks.set(argsKey, debouncedCallback);
            this.cancelAllCallbacks(argsKey);
            debouncedCallback(path);
        };
    }

    /** Drops every queued hover intent, for a caller whose decision is already made. */
    cancelQueuedCallbacks() {
        this.callbacks.forEach((queued) => queued.cancel());
        this.callbacks.clear();
    }

    cancelAllCallbacks(nextCallbackArgsKey: string) {
        this.callbacks.forEach((callback, previousCallbackKey) => {
            if (nextCallbackArgsKey === previousCallbackKey) {
                return;
            }
            const path = this._getPath(previousCallbackKey).split('-');
            const sharesPath = path.some((p) => nextCallbackArgsKey.indexOf(p) >= 0);
            sharesPath && callback.cancel();
            this.callbacks.delete(previousCallbackKey);
        });
    }
}

const debounceManager = new DebouncedSetMenuPathManager(MOUSEENTER_DELAY_MS);

type DefaultToggleProps = {
    text?: string;
    onClick: VoidFunction;
    'aria-expanded'?: Booleanish;
    ref?: React.Ref<HTMLButtonElement>;
};
const DefaultToggle = React.memo(function DefaultToggle({
    text,
    onClick,
    'aria-expanded': ariaExpanded,
    ref,
    ...props
}: DefaultToggleProps) {
    return (
        <Button ref={ref} onClick={onClick} text={text} aria-expanded={ariaExpanded} {...props} />
    );
});

type NestedToggleProps = {
    text: string;
    onClick: (e: React.MouseEvent) => void;
    triggerOnHover?: boolean;
    'aria-expanded'?: Booleanish;
    icon?: string;
    iconClassName?: string;
    className?: string;
    ref?: React.Ref<HTMLAnchorElement>;
};
const NestedToggle = React.memo(function NestedToggle({
    text,
    onClick,
    triggerOnHover = true,
    'aria-expanded': ariaExpanded,
    icon = '',
    iconClassName = 'material-symbols-outlined',
    className = '',
    ref,
    ...props
}: NestedToggleProps) {
    const onMouseEnter = React.useCallback(
        (e: React.MouseEvent) => {
            triggerOnHover && onClick(e);
        },
        [onClick, triggerOnHover],
    );

    let iconNode = null;
    if (icon) {
        iconNode = (
            <i translate="no" className={`icon-left ${iconClassName}`}>
                {icon}
            </i>
        );
    }
    return (
        <a
            className={`dropdown-item nested-toggle d-flex ${className}`}
            onMouseEnter={onMouseEnter}
            ref={ref}
            onClick={onClick}
            aria-expanded={ariaExpanded}
            {...props}
        >
            {iconNode}
            {text}
            <i translate="no" className="material-symbols-outlined ms-l5-auto icon-right">
                chevron_right
            </i>
        </a>
    );
});

const MULTITEXT_ITEM_WIDTH = 18; // similar to grid system: if there are 2 items, each item takes up 24 / 2 = 12rem

const DropdownItem = React.memo(function DropdownItem({
    text,
    onClick,
    onSelectData,
    onSelect,
    icon = '',
    iconClassName,
    className,
    textStyle,
    children,
    disabled = false,
    href,
    active,
    as: Component,
    textArray = NO_MULTI_TEXT,
    ref,
    ...props
}: {
    id: string;
    text?: string;
    onClick?: (event: React.MouseEvent) => void;
    onMouseEnter?: (event: React.MouseEvent) => void;
    onSelectData?: any;
    onSelect?: (data: any, event: React.MouseEvent) => void;
    icon?: string;
    iconClassName?: string;
    className?: string;
    textStyle?: DROPDOWN_ITEM_TEXT_STYLES;
    children?: React.ReactNode;
    disabled?: boolean;
    href?: string;
    active?: boolean;
    as?: React.ComponentType<any>;
    textArray?: readonly PlainDropdownItemMultiText[];
    ref?: React.Ref<HTMLAnchorElement>;
    [passThroughProp: string]: unknown;
}) {
    const onSelectCtx = React.useContext(SelectableContext);

    const handleClick = React.useCallback(
        (event: React.MouseEvent) => {
            if (disabled) {
                return;
            }
            if (onClick) {
                onClick(event);
            }
            if (onSelectCtx) {
                // sends the onSelect call to the parent dropdown
                onSelectCtx(onSelectData, event);
            }
            if (onSelect) {
                onSelect(onSelectData, event);
            }
        },
        [disabled, onClick, onSelect, onSelectCtx, onSelectData],
    );

    let iconNode = null;
    if (icon) {
        if (icon === SPINNER_ICON) {
            iconNode = (
                <div className="icon-left">
                    <Spinner size="sm" className={iconClassName} />
                </div>
            );
        } else {
            iconNode = (
                <i
                    translate="no"
                    className={classNames('icon-left material-symbols-outlined', iconClassName)}
                >
                    {CUSTOM_ICONS_MAP[icon] || icon}
                </i>
            );
        }
    }

    const expectedProps = {
        href,
        ref,
        onClick: handleClick,
        className: classNames('dropdown-item', className, textStyle, {
            'icon-dropdown-item': !!icon,
            disabled: disabled,
            active: active,
        }),
    };

    const innerContent = (
        <>
            {iconNode}
            {text}
            {children}
        </>
    );

    if (Component) {
        return (
            <Component {...expectedProps} {...props}>
                {innerContent}
            </Component>
        );
    } else if (textArray.length > 0) {
        const width = Math.floor(MULTITEXT_ITEM_WIDTH / textArray.length);
        const multiTextNodes = textArray.map((textObj, idx) => {
            let content = null;
            const MultiTextComponent = textObj.component as
                | React.ComponentType<Record<string, unknown>>
                | undefined;
            if (MultiTextComponent) {
                content = <MultiTextComponent {...textObj.props} key={`component-${idx}`} />;
            } else {
                content = textObj.text;
            }
            return (
                <span
                    style={{
                        width: `${width}rem`,
                    }}
                    key={`${textObj.text}-${idx}`}
                    className={classNames(textObj.className, textObj.textStyle)}
                >
                    {content}
                </span>
            );
        });
        return (
            <a
                {...expectedProps}
                {...props}
                className={classNames(
                    'dropdown-item-multitext align-items-center',
                    expectedProps.className,
                )}
            >
                {multiTextNodes}
            </a>
        );
    } else {
        return (
            <a {...expectedProps} {...props}>
                {innerContent}
            </a>
        );
    }
});

export type PlainDropdownItemMultiText = Readonly<{
    text?: string;
    textStyle?: DROPDOWN_ITEM_TEXT_STYLES;
    className?: string;
    component?: React.ComponentType<never>;
    props?: Readonly<Record<string, unknown>>;
}>;

const NO_MULTI_TEXT: readonly PlainDropdownItemMultiText[] = [];

export const DROPDOWN_DIVIDER = 'DROPDOWN_DIVIDER';
export const DROPDOWN_HEADER = 'DROPDOWN_HEADER';

const withoutKeys = <Props extends object, Key extends keyof Props>(
    props: Props,
    ignoreKeys: readonly Key[],
): Omit<Props, Key> => {
    const spreadable = { ...props } as Record<PropertyKey, unknown>;
    ignoreKeys.forEach((key) => delete spreadable[key]);
    return spreadable as Omit<Props, Key>;
};

type DropdownItemFields<SelectDataType extends object> = {
    id: string;
    text?: string;
    onSelectData?: SelectDataType;
    tooltipTitle?: string;
    tooltipContainer?: string;
    tooltipPlacement?: Placement;
    icon?: string;
    disabled?: boolean;
    itemComponent?: React.ComponentType<never>;
    nestedDropdown?: PlainNestedDropdownProps<SelectDataType>;
    className?: string;
    textStyle?: DROPDOWN_ITEM_TEXT_STYLES;
    iconClassName?: string;
    'data-testid'?: string;
    children?: React.ReactNode;
    href?: string;
};

export type PlainNestedDropdownProps<SelectDataType extends object> = Readonly<
    Omit<DropdownProps<SelectDataType>, 'items' | 'onItemSelect'> & {
        items?: readonly PlainDropdownItem<SelectDataType>[];
    }
>;

export type PlainDropdownItem<SelectDataType extends object> = Readonly<
    Omit<DropdownItemFields<SelectDataType>, 'nestedDropdown'> & {
        nestedDropdown?: PlainNestedDropdownProps<SelectDataType>;
        textArray?: readonly PlainDropdownItemMultiText[];
    }
>;

export type DropdownProps<SelectDataType extends object> = {
    items?: readonly PlainDropdownItem<SelectDataType>[]; // the list of items (and the props to pass into each item)
    onItemSelect?: (onSelectData: SelectDataType, e: React.MouseEvent) => void /*
     * The callback when one of the items is selected.
     * Pass `onSelectData` into each of the entries in `items`
     * to receive the data back as an argument to `onItemSelect`.
     */;
    as?: React.ElementType; // The top-level type of element/component that should be rendered
    drop?: 'up' | 'down' | 'start' | 'end'; // the direction that the dropdown should drop
    closeOnSelect?: boolean; // whether the dropdown should close when one of the items in the dropdown is clicked
    ToggleComponent?: React.ComponentType<any>; // the Component to use to render the Toggle. Must accept a ref prop.
    toggleProps?: Readonly<Record<string, unknown>>; // the props to pass into the ToggleComponent
    MenuComponent?: React.ComponentType; // the component to use to render the Menu
    menuProps?: Readonly<Record<string, unknown>>; // the props to pass into the MenuComponent
    ItemComponent?: React.ComponentType<any>; // the component to use to render each Item
    breakOverflow?: boolean; // whether to use fixed positioning strategy or not: https://popper.js.org/docs/v2/constructors/#strategy
    limitMenuHeight?: boolean; // whether to force the menu to scroll if it takes more space than a given maximum height
    afterOpen?: (path: string) => void;
    afterClose?: () => void;
    className?: string; // the top-level className to apply to the dropdown
    'data-testid'?: string;
    nested?: boolean;
    setParentActiveMenuPath?: (path: string, delay: number) => void;
    parentActiveMenuPath?: string;
    myMenuPath?: string;
    ref?: React.Ref<unknown>;
};
export type DropdownRef = {
    open: (path: string) => void;
    close: () => void;
};

/**
 * The Dropdown component is very flexible. You can do all sorts of things, like:
 *
 *   - render using a custom toggle component
 *   - render using a custom menu component
 *   - render using a custom item component
 *
 * But there are also defaults that provide a "normal" Lumen5 dropdown.
 *
 * Probably the best way to use the Dropdown is to keep `items` stable (a module constant, or
 * memoized) and include an `onSelectData` field along with each item.
 * You can use this `onSelectData` to perform operations when the `onItemSelect` callback is
 * called. For example:
 *
 * // create an enum of action types
 * const actionTypes = {ONE: 'one', TWO: 'two'};
 *
 * // make these consts or memoize them to avoid excessive re-renders
 * const ITEMS: readonly PlainDropdownItem<YOUR_SELECT_DATA_TYPE>[] = [
 *     {
 *         id: 'one',
 *         text: 'item one!',
 *         onSelectData: {type: actionTypes.ONE, otherMetadata: 1},
 *     },
 *     {
 *         id: 'two',
 *         text: 'item two!',
 *         onSelectData: {type: actionTypes.TWO, otherMetadata: 2},
 *     },
 * ];
 *
 * // then render the Dropdown wherever you need
 * <Dropdown
 *   items={ITEMS}
 *   onItemSelect={onItemSelect}
 *   {...props}
 *   />
 *
 * // and finally, we can make a reducer-esque onItemSelect callback:
 * const onItemSelect = React.useCallback((onSelectData: YOUR_SELECT_DATA_TYPE) {
 *     switch(onSelectData.type) {
 *         case actionTypes.ONE: {
 *             // handle action one!
 *         }
 *         case actionTypes.TWO: {
 *             // handle action two!
 *         }
 *     }
 * });
 *
 * It's probably best to look at Dropdown.stories.tsx for more examples of ways to customize!
 */
function DropdownFunc<SelectDataType extends object>({
    as,
    drop,
    closeOnSelect = true,
    ToggleComponent,
    toggleProps,
    MenuComponent,
    menuProps,
    ItemComponent,
    items,
    breakOverflow = false,
    limitMenuHeight = false,
    onItemSelect,
    afterOpen,
    afterClose,
    className,
    'data-testid': dataTestId = 'dropdown',

    // internal props, used for nested dropdowns.
    // external users of the <Dropdown/> should ignore these props:
    nested = false,
    setParentActiveMenuPath,
    parentActiveMenuPath = '',
    myMenuPath = 'menu',
    ref,
}: DropdownProps<SelectDataType> & { ref?: React.Ref<DropdownRef> }) {
    /*
     * Below is all the code to handle the open/close state of the dropdown,
     * including nesting state.
     *
     * Whether or not the dropdown is showing is driven by a "menuPath". Each
     * Item inside of a dropdown has a menuPath. For nested items, the menuPath
     * is the concatenation: `${parentMenuPath}-${myMenuPath}`.
     *
     * The top-level Dropdown then maintains the currently open menuPath (the
     * activeMenuPath). Nested dropdowns do not maintain this state, they just
     * use the value from the top-level parent.
     */
    const [_activeMenuPath, _setActiveMenuPath] = React.useState('');
    const debouncedSetActiveMenuPath = (newMenuPath: string, debounceDelay: number) =>
        debounceManager.addCallback(_setActiveMenuPath, debounceDelay)(newMenuPath);

    // a setter and getter for the active menu path
    const getActiveMenuPath = React.useCallback(() => {
        if (nested) {
            return parentActiveMenuPath;
        } else {
            return _activeMenuPath;
        }
    }, [_activeMenuPath, nested, parentActiveMenuPath]);

    const setActiveMenuPath = React.useCallback(
        (newMenuPath: string, debounceTime: number = 0) => {
            if (nested) {
                typeof setParentActiveMenuPath === 'function' &&
                    setParentActiveMenuPath(newMenuPath, MOUSEENTER_DELAY_MS);
            } else {
                debouncedSetActiveMenuPath(newMenuPath, debounceTime);
            }
        },
        [nested, setParentActiveMenuPath],
    );

    /**
     * The path a caller asked for through the ref, applied without going through
     * the debounce manager. That manager exists to let an accidental hover be
     * overtaken, and it is shared by every dropdown on the page — a queued path
     * is cancelled as soon as any other dropdown enqueues one of its own. A
     * caller opening a menu has already decided, so it cannot be left waiting in
     * a queue where an unrelated dropdown can drop it on the floor.
     */
    const applyActiveMenuPathNow = React.useCallback(
        (newMenuPath: string) => {
            if (nested) {
                typeof setParentActiveMenuPath === 'function' &&
                    setParentActiveMenuPath(newMenuPath, MOUSEENTER_DELAY_MS);
                return;
            }
            debounceManager.cancelQueuedCallbacks();
            _setActiveMenuPath(newMenuPath);
        },
        [nested, setParentActiveMenuPath],
    );

    // the toggling logic
    const show = getActiveMenuPath().indexOf(myMenuPath) >= 0;
    const onToggle = React.useCallback(
        (requestOpen: boolean, metadata: ToggleMetadata) => {
            if (metadata.source !== 'rootClose') {
                setActiveMenuPath(myMenuPath);
            }

            if (closeOnSelect && !requestOpen && metadata.source === 'select') {
                setActiveMenuPath('');
            }

            if (!requestOpen && metadata.source === 'rootClose') {
                setActiveMenuPath('');
            }
        },
        [closeOnSelect, setActiveMenuPath, myMenuPath],
    );

    React.useEffect(() => {
        if (show) {
            typeof afterOpen === 'function' && afterOpen(myMenuPath);
        } else {
            typeof afterClose === 'function' && afterClose();
        }
    }, [afterClose, afterOpen, myMenuPath, show]);

    /*
     * this provides a way for parents that instantiate a Dropdown to
     * instruct the dropdown to close. Can be used like:
     *
     *  dropdownRef.current.close();
     */
    React.useImperativeHandle(ref, () => {
        return {
            open: (path: string) => {
                applyActiveMenuPathNow(path);
            },
            close: () => {
                applyActiveMenuPathNow('');
            },
        };
    });

    /*
     * Here is all the code to handle what gets rendered!
     */
    let toggleAs = ToggleComponent;
    if (!toggleAs) {
        if (nested) {
            toggleAs = NestedToggle;
        } else {
            toggleAs = DefaultToggle;
        }
    }

    if (nested && drop === undefined) {
        drop = 'end';
    }

    const spreadableMenuProps: Record<string, unknown> = { ...menuProps };
    if (limitMenuHeight) {
        spreadableMenuProps.className = classNames(
            spreadableMenuProps.className as string | undefined,
            {
                'limit-menu-height': limitMenuHeight,
            },
        );
    }

    let propOverrides = {};
    if (breakOverflow) {
        propOverrides = {
            popperConfig: {
                strategy: 'fixed',
            },
            // if using strategy: 'fixed', there's a react-bootstrap bug that also means we need renderOnMount
            // https://github.com/react-bootstrap/react-bootstrap/issues/6203
            // If that issue is solved, we can probably remove renderOnMount from propOverrides
            renderOnMount: true,
        };
    }

    const handleDropdownItemMouseEnter = React.useCallback(
        (e: React.MouseEvent) => {
            setActiveMenuPath(myMenuPath, MOUSEENTER_DELAY_MS);
        },
        [myMenuPath, setActiveMenuPath],
    );

    return (
        <ReactBootstrapDropdown
            as={as}
            drop={drop}
            className={classNames(className, styles['lumen5-dropdown'])}
            data-testid={dataTestId}
            show={show}
            onToggle={onToggle}
            onSelect={onItemSelect as any} // the compiler does not know that we are supplying our own component and the SelectCallback type is not valid
        >
            <ReactBootstrapDropdown.Toggle as={toggleAs} {...toggleProps} />
            <ReactBootstrapDropdown.Menu
                as={MenuComponent || undefined}
                {...spreadableMenuProps}
                {...propOverrides}
            >
                {/* Google Translate replaces the text nodes it translates with its own
                    <font> elements, leaving React holding references to nodes that are no
                    longer in the document. Removing a menu item then fails with
                    "removeChild: the node to be removed is not a child of this node".
                    translate="no" is inherited by the whole subtree, so marking the item
                    container keeps every label out of Translate's reach. */}
                <div
                    translate="no"
                    className={classNames('scroller-fix', {
                        'px-l5-4': nested,
                    })}
                >
                    {items &&
                        (Array.from<PlainDropdownItem<SelectDataType>, React.ReactNode>(
                            items,
                            (itemProps, index) => {
                                if (itemProps.nestedDropdown) {
                                    return (
                                        <Dropdown
                                            key={itemProps.id}
                                            nested={true}
                                            setParentActiveMenuPath={setActiveMenuPath}
                                            parentActiveMenuPath={getActiveMenuPath()}
                                            myMenuPath={`${myMenuPath}-${itemProps.id}`}
                                            {...itemProps.nestedDropdown}
                                        />
                                    );
                                }
                                if (itemProps.id === DROPDOWN_DIVIDER) {
                                    return (
                                        <ReactBootstrapDropdown.Divider
                                            key={DROPDOWN_DIVIDER + index.toString()}
                                        />
                                    );
                                }
                                if (itemProps.id === DROPDOWN_HEADER) {
                                    if (itemProps.text && itemProps.text !== '') {
                                        return (
                                            <ReactBootstrapDropdown.Header
                                                key={DROPDOWN_HEADER + index.toString()}
                                                className={classNames(
                                                    'dropdown-header-container',
                                                    itemProps.className,
                                                )}
                                            >
                                                <>
                                                    {itemProps.icon && (
                                                        <i
                                                            translate="no"
                                                            className="icon-left material-symbols-outlined"
                                                        >
                                                            {itemProps.icon as React.ReactNode}
                                                        </i>
                                                    )}
                                                    {itemProps.text}
                                                </>
                                            </ReactBootstrapDropdown.Header>
                                        );
                                    }
                                } else {
                                    // if wrapped with <Tooltip/>
                                    const tooltipTitle = itemProps.tooltipTitle;
                                    if (tooltipTitle) {
                                        return (
                                            <Tooltip
                                                key={`tooltip-'${itemProps.id}`}
                                                title={tooltipTitle}
                                                container={itemProps.tooltipContainer}
                                                placement={itemProps.tooltipPlacement}
                                            >
                                                {/* bootstrap will add attributes to the element that the tooltip text needs to be
                                            appended to. However, for components that have a disabled state, these attributes
                                            don't seem to show if the item is in the disabled state. A workaround is to wrap
                                            a container so the tooltip attributes will be modified on the <div/>, and the
                                            tooltip will show even if the component item is disabled. */}
                                                {/* same issue appears in <Button/> */}
                                                <div>
                                                    <DropdownItem
                                                        key={itemProps.id}
                                                        as={
                                                            (itemProps.itemComponent as
                                                                | React.ComponentType<any>
                                                                | undefined) || ItemComponent
                                                        }
                                                        onMouseEnter={handleDropdownItemMouseEnter}
                                                        {...withoutKeys(itemProps, [
                                                            'itemComponent',
                                                            'tooltipTitle',
                                                            'tooltipContainer',
                                                            'tooltipPlacement',
                                                        ])}
                                                    />
                                                </div>
                                            </Tooltip>
                                        );
                                    }

                                    return (
                                        <DropdownItem
                                            key={itemProps.id}
                                            as={
                                                (itemProps.itemComponent as
                                                    | React.ComponentType<any>
                                                    | undefined) || ItemComponent
                                            }
                                            onMouseEnter={handleDropdownItemMouseEnter}
                                            {...withoutKeys(itemProps, [
                                                'itemComponent',
                                                'tooltipTitle',
                                                'tooltipContainer',
                                                'tooltipPlacement',
                                            ])}
                                        />
                                    );
                                }
                            },
                        ) as React.ReactNode)}
                </div>
            </ReactBootstrapDropdown.Menu>
        </ReactBootstrapDropdown>
    );
}

export const Dropdown = <T extends object>({
    myRef,
    ...rest
}: DropdownProps<T> & { myRef?: React.Ref<DropdownRef> }) => <DropdownFunc {...rest} ref={myRef} />;
